<script setup lang="ts">
import { debounce } from "lodash-es";
import { ref, onMounted, watch } from "vue";
import { useI18n } from "vue-i18n";

import { baseDate, baseHour, baseTextarea } from "@/common/components";
import moment from "moment";

const { t } = useI18n();

const emit = defineEmits(["update-data"]);

const props = withDefaults(
  defineProps<{
    data: { date: string; hour: string; motive: string } | null;
    width?: string;
    patch?: boolean;
  }>(),
  {
    width: "100%",
    patch: false,
  }
);

const payload = ref<{
  date: string | null;
  hour: string | null;
  motive: string | null;
}>({
  date: moment().format("YYYY-MM-DD"),
  hour: moment().format("HH:mm:ss"),
  motive: "",
});

function update(key: keyof typeof payload.value) {
  if (props.patch) {
    emit("update-data", { [key]: payload.value[key] });
    return;
  }

  emit("update-data", {
    ...payload.value,
    [key]: payload.value[key],
  });
}

function formatData() {
  if (!props.data?.date) {
    return;
  }

  payload.value = {
    date: props.data.date,
    hour: props.data.hour,
    motive: props.data?.motive || "",
  };
}

const debounceForm = debounce(update, 300);

watch(
  () => props.data,
  () => {
    formatData();
  }
);

onMounted(() => {
  formatData();

  emit("update-data", payload.value);
});
</script>

<template>
  <section class="form-content" :style="{ width: width }">
    <form>
      <div class="date-hour">
        <base-date
          v-model="payload.date"
          :value="payload.date"
          :required="true"
          value-mask="YYYY-MM-DD"
          placeholder="--/--/----"
          @update:model-value="update('date')"
        >
          <template #label> {{ t("common.labels.date") }} *</template>
        </base-date>

        <base-hour
          v-model="payload.hour"
          :value="payload.hour"
          :required="true"
          value-mask="HH:mm:ss"
          placeholder="--:--:--"
          @update:model-value="update('hour')"
        >
          <template #label> {{ t("common.labels.hour") }} *</template>
        </base-hour>
      </div>

      <base-textarea
        v-model="payload.motive"
        :value="payload.motive"
        :placeholder="t('common.placeholders.describe')"
        @input="debounceForm('motive')"
      >
        <template #label> {{ t("common.labels.motive") }} * </template>
      </base-textarea>
    </form>
  </section>
</template>

<style lang="scss" scoped>
.form-content {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  form {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    .date-hour {
      display: flex;
      flex-direction: row;
      gap: 1rem;
    }
  }
}
</style>
